import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { CargaGlobal } from '@/components/Cargando';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { loginQueRecuerda } from '@/lib/destinoDeEntrada';
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import "@/i18n/reactPatch";
import { LocaleProvider } from "@/contexts/LocaleContext";
import { Toaster } from "@/components/ui/sonner";
import SessionInactivityGuard from "@/components/auth/SessionInactivityGuard";

// Layouts
import AdminLayout from "@/layouts/AdminLayout";
import TenantLayout from "@/layouts/TenantLayout";

// Pages
import Login from "@/pages/Login";
import LandingPage from "@/pages/LandingPage";
import LandingDiagnosis from "@/pages/LandingDiagnosis";
import CavernicolAppLanding from "@/pages/tenant/CavernicolAppLanding";
import TermsOfService from "@/pages/legal/TermsOfService";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import AcceptInvite from "@/pages/AcceptInvite";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AdminDashboard from "@/pages/admin/Dashboard";
import Tenants from "@/pages/admin/Tenants";
import TenantDetail from "@/pages/admin/TenantDetail";
import AuditLogs from "@/pages/admin/AuditLogs";
import Verticals from "@/pages/admin/Verticals";
import UserTypes from "@/pages/admin/UserTypes";
import PlanBuilder from "@/pages/admin/PlanBuilder";
import AdminAddons from "@/pages/admin/Addons";
import UsageDashboard from "@/pages/admin/UsageDashboard";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminUsers from "@/pages/admin/Users";
import SupportTickets from "@/pages/admin/SupportTickets";
import TenantSubscription from "@/pages/admin/TenantSubscription";
import InfrastructureConfig from "@/pages/admin/InfrastructureConfig";
import GupshupPartner from "@/pages/admin/GupshupPartner";
import LandingConfig from "@/pages/admin/LandingConfig";
import AdminRoadmap from "@/pages/admin/Roadmap";
import PlatformMessages from "@/pages/admin/PlatformMessages";
import Billing from "@/pages/admin/Billing";
import Cobranza from "@/pages/admin/Cobranza";
import PortalSocio from "@/pages/partner/PortalSocio";
const PartnerPlanBuilder = lazy(() => import("@/pages/partner/PartnerPlanBuilder"));
import Socios from "@/pages/admin/Socios";
import CostRates from "@/pages/admin/CostRates";
import WebDashboard from "@/pages/tenant/web/WebDashboard";
import WebSiteNew from "@/pages/tenant/web/WebSiteNew";
import WebSiteEditor from "@/pages/tenant/web/WebSiteEditor";
import WebTracking from "@/pages/tenant/web/WebTracking";
import WebDomains from "@/pages/tenant/web/WebDomains";
import WebVersions from "@/pages/tenant/web/WebVersions";
import WebForms from "@/pages/tenant/web/WebForms";
import WebDomainsInfra from "@/pages/admin/WebDomainsInfra";
import TenantDashboard from "@/pages/tenant/Dashboard";
import OnboardingWizard from "@/pages/tenant/OnboardingWizard";
import AIQuickStart from "@/pages/tenant/AIQuickStart";
import LeadAttendanceSetup from "@/pages/tenant/LeadAttendanceSetup";
import Locations from "@/pages/tenant/Locations";
import BusinessConnections from "@/pages/tenant/BusinessConnections";
import BusinessPermissions from "@/pages/tenant/BusinessPermissions";
import MyPlan from "@/pages/tenant/MyPlan";
import IntegrationCenter from "@/pages/tenant/IntegrationCenter";
import IntegrationPurposes from "@/pages/tenant/IntegrationPurposes";
import EvolutionPanel from "@/pages/tenant/EvolutionPanel";
import WhatsAppProxies from "@/pages/tenant/WhatsAppProxies";
import LeadSources from "@/pages/tenant/LeadSources";
import LeadSourceDetail from "@/pages/tenant/LeadSourceDetail";
import AITestConsole from "@/pages/tenant/AITestConsole";
import AIDashboard from "@/pages/tenant/AIDashboard";
import AIRoutingConfig from "@/pages/tenant/AIRoutingConfig";
import AdminAIDashboard from "@/pages/admin/AdminAIDashboard";
import EmailTemplates from "@/pages/tenant/EmailTemplates";
import AITrainingStudio from "@/pages/tenant/AITrainingStudio";
import AIHubLayout from "@/layouts/AIHubLayout";
import KnowledgeLibrary from "@/pages/tenant/KnowledgeLibrary";
import AISkillBuilder from "@/pages/tenant/AISkillBuilder";
import AIToolRegistry from "@/pages/tenant/AIToolRegistry";
import MCPConnectorCenter from "@/pages/tenant/MCPConnectorCenter";
import ActionOrchestrator from "@/pages/tenant/ActionOrchestrator";
import TriggerCenter from "@/pages/tenant/TriggerCenter";
import VoiceCallCenter from "@/pages/tenant/VoiceCallCenter";
import ObservabilityCenter from "@/pages/tenant/ObservabilityCenter";
import AttendanceStatus from "@/pages/tenant/AttendanceStatus";
import MarketingHubLayout from "@/layouts/MarketingHubLayout";
import SocialAdsHubLayout from "@/layouts/SocialAdsHubLayout";
import ReportsHubLayout from "@/layouts/ReportsHubLayout";
import BusinessHubLayout from "@/layouts/BusinessHubLayout";
import AdministracionHubLayout from "@/layouts/AdministracionHubLayout";
import TeamManager from "@/pages/tenant/TeamManager";
import CatalogHub from "@/pages/tenant/CatalogHub";
import PosHub from "@/pages/tenant/PosHub";
import WarrantyTickets from "@/pages/tenant/WarrantyTickets";
import CondoManagement from "@/pages/tenant/CondoManagement";
import FlowBuilder from "@/pages/tenant/FlowBuilder";
import DevelopmentManager from "@/pages/tenant/DevelopmentManager";
import HotelDesk from "@/pages/tenant/HotelDesk";
import WorkshopDesk from "@/pages/tenant/WorkshopDesk";
import ClinicDesk from "@/pages/tenant/ClinicDesk";
import RestaurantService from "@/pages/tenant/RestaurantService";
import SpaDesk from "@/pages/tenant/SpaDesk";
import BusinessVerticalProfile from "@/pages/tenant/BusinessVerticalProfile";
import BusinessResumen from "@/pages/tenant/BusinessResumen";
import CampaignBuilder from "@/pages/tenant/CampaignBuilder";
import CampaignsQR from "@/pages/tenant/CampaignsQR";
import LeadOriginsHub from "@/pages/tenant/LeadOriginsHub";
import ContactManager from "@/pages/tenant/ContactManager";
import AudienceBuilder from "@/pages/tenant/AudienceBuilder";
import CampaignScheduler from "@/pages/tenant/CampaignScheduler";
import CampaignDelivery from "@/pages/tenant/CampaignDelivery";
import MarketingEmailHub from '@/pages/tenant/MarketingEmailHub';
import EmailHubLayout from '@/layouts/EmailHubLayout';
import WhatsAppHubLayout from '@/layouts/WhatsAppHubLayout';
import EmailHome from '@/pages/tenant/EmailHome';
import EmailInformes from '@/pages/tenant/EmailInformes';
import WhatsAppHome from '@/pages/tenant/WhatsAppHome';
import WhatsAppConexion from '@/pages/tenant/WhatsAppConexion';
import WhatsAppInformes from '@/pages/tenant/WhatsAppInformes';
import EmailMassSendWizard from '@/pages/tenant/EmailMassSendWizard';
import MarketingEnvios from '@/pages/tenant/MarketingEnvios';
import MarketingWhatsAppHub from '@/pages/tenant/MarketingWhatsAppHub';
import MarketingHome from "@/pages/tenant/MarketingHome";
import SocialPublishing from "@/pages/tenant/SocialPublishing";
import MetaAdsConfig from "@/pages/tenant/MetaAdsConfig";
import MarketingTemplates from "@/pages/tenant/MarketingTemplates";
import MarketingSegments from "@/pages/tenant/MarketingSegments";
import MarketingAutomations from "@/pages/tenant/MarketingAutomations";
import MarketingSimpleFlow from "@/pages/tenant/MarketingSimpleFlow";
import LeadCaptureHub from "@/pages/tenant/LeadCaptureHub";
import LeadCaptureWizard from "@/pages/tenant/LeadCaptureWizard";
import CrmHubLayout from "@/layouts/CrmHubLayout";
import CrmPipeline from "@/pages/tenant/CrmPipeline";
import CrmLeads from "@/pages/tenant/CrmLeads";
import CrmTasks from "@/pages/tenant/CrmTasks";
import CrmInboundPending from "@/pages/tenant/CrmInboundPending";
import CrmAssignment from "@/pages/tenant/CrmAssignment";
import CrmContactTimeline from "@/pages/tenant/CrmContactTimeline";
import CrmPlantillas from "@/pages/tenant/CrmPlantillas";
import AgendaHubLayout from "@/layouts/AgendaHubLayout";
import AgendaNew from "@/pages/tenant/AgendaNew";
import AgendaResources from "@/pages/tenant/AgendaResources";
import RestaurantFloorPlan from "@/pages/tenant/RestaurantFloorPlan";
import AgendaCalendarUnified from "@/pages/tenant/AgendaCalendarUnified";
import InboxHubLayout from "@/layouts/InboxHubLayout";
import Inbox from "@/pages/tenant/Inbox";
import VerticalRouteGuard from "@/components/VerticalRouteGuard";

function ProtectedRoute({ children, requirePlatform, requireTenant , requirePartner }) {
  const { user, loading } = useAuth();
  const ubicacion = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F8F9FA]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" />
          <p className="text-sm text-[#64748B]">Cargando...</p>
        </div>
      </div>
    );
  }

  // Se recuerda a dónde iba. Antes se tiraba el destino, y quien llegaba por un
  // enlace de WhatsApp acababa en el tablero buscando a mano lo que el enlace
  // ya sabía.
  if (!user) {
    return <Navigate to={loginQueRecuerda(ubicacion.pathname + ubicacion.search)} replace />;
  }
  // S134 (4/N) — el socio es la tercera figura: ni plataforma ni negocio. Sin
  // esta línea acababa en /tenant, donde el servidor lo rechaza, y veía una
  // pantalla rota en vez de la suya.
  if (user.user_type === 'partner' && !requirePartner) return <Navigate to="/socio" replace />;
  if (requirePartner && user.user_type !== 'partner') {
    return <Navigate to={user.user_type === 'platform' ? '/admin' : '/tenant'} replace />;
  }
  if (requirePlatform && user.user_type !== 'platform') return <Navigate to="/tenant" replace />;
  if (requireTenant && user.user_type === 'platform' && !user.tenant_id) return <Navigate to="/admin" replace />;

  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F8F9FA]">
        <div className="w-10 h-10 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={
        user ? <Navigate to={user.user_type === 'platform' ? '/admin'
                             : user.user_type === 'partner' ? '/socio' : '/tenant'} replace /> : <Login />
      } />

      {/* Public - Accept Invitation */}
      <Route path="/invite/:token" element={<AcceptInvite />} />

      {/* Public - Password Reset */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />

      {/* Portal del socio — S134 (4/N). Ni Super Admin ni panel de negocio. */}
      <Route path="/socio/planes" element={
        <ProtectedRoute requirePartner>
          <Suspense fallback={<div className="p-8 text-sm text-slate-500">Cargando constructor…</div>}>
            <PartnerPlanBuilder />
          </Suspense>
        </ProtectedRoute>
      } />
      <Route path="/socio" element={
        <ProtectedRoute requirePartner>
          <PortalSocio />
        </ProtectedRoute>
      } />
      {/* Enlaces de recarga (cron) y docs antiguos usaban /partner/mensajes. */}
      <Route path="/partner/mensajes" element={
        <ProtectedRoute requirePartner>
          <Navigate to="/socio#mensajes" replace />
        </ProtectedRoute>
      } />
      <Route path="/partner/*" element={
        <ProtectedRoute requirePartner>
          <Navigate to="/socio" replace />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute requirePlatform>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="tenants" element={<Tenants />} />
        <Route path="tenants/:id" element={<TenantDetail />} />
        <Route path="tenants/:id/subscription" element={<TenantSubscription />} />
        <Route path="plans" element={<PlanBuilder />} />
        <Route path="addons" element={<AdminAddons />} />
        <Route path="usage" element={<UsageDashboard />} />
        <Route path="ai" element={<AdminAIDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="tickets" element={<SupportTickets />} />
        <Route path="audit" element={<AuditLogs />} />
        <Route path="verticals" element={<Verticals />} />
        <Route path="user-types" element={<UserTypes />} />
        <Route path="infrastructure" element={<InfrastructureConfig />} />
        <Route path="gupshup-partner" element={<GupshupPartner />} />
        <Route path="landing" element={<LandingConfig />} />
        <Route path="web-domains" element={<WebDomainsInfra />} />
        <Route path="mensajes" element={<PlatformMessages />} />
        <Route path="cobros" element={<Billing />} />
        <Route path="cobranza" element={<Cobranza />} />
        <Route path="socios" element={<Socios />} />
        <Route path="tarifario" element={<CostRates />} />
        <Route path="roadmap" element={<AdminRoadmap />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>

      {/* Tenant Routes */}
      <Route path="/tenant/onboarding" element={
        <ProtectedRoute>
          <OnboardingWizard />
        </ProtectedRoute>
      } />
      <Route path="/tenant" element={
        <ProtectedRoute>
          <TenantLayout />
        </ProtectedRoute>
      }>
        <Route index element={<TenantDashboard />} />
        <Route path="setup" element={<LeadAttendanceSetup />} />
        <Route path="plan" element={<MyPlan />} />
        <Route path="crm" element={<CrmHubLayout />}>
          {/* El Pipeline es la primera vista del CRM. La ruta /pipeline se
              conserva apuntando al mismo componente: hay enlaces internos y
              marcadores de usuarios que la usan, y romperlos por mover una
              pestana seria cambiar una molestia por otra. */}
          <Route index element={<CrmPipeline />} />
          <Route path="pipeline" element={<CrmPipeline />} />
          <Route path="leads" element={<CrmLeads />} />
          <Route path="tasks" element={<CrmTasks />} />
          <Route path="plantillas" element={<CrmPlantillas />} />
          <Route path="pending" element={<CrmInboundPending />} />
          <Route path="contacts/:contactId" element={<CrmContactTimeline />} />
        </Route>
        <Route path="agenda" element={<AgendaHubLayout />}>
          {/* El calendario es la primera vista. /calendar se conserva:
              hay enlaces internos y marcadores que la usan. */}
          <Route index element={<AgendaCalendarUnified />} />
          {/* Sprint 53 (5/N) — Hoy/Semana/Todas/Calendario-por-recurso
              fusionados en un solo calendario día/semana/mes; las rutas
              viejas redirigen para no romper enlaces/marcadores guardados. */}
          <Route path="today" element={<Navigate to="/tenant/agenda/calendar" replace />} />
          <Route path="week" element={<Navigate to="/tenant/agenda/calendar" replace />} />
          <Route path="all" element={<Navigate to="/tenant/agenda/calendar" replace />} />
          <Route path="new" element={<AgendaNew />} />
          <Route path="resources" element={<Navigate to="/tenant/business/catalog" replace />} />
          <Route path="floor-plan" element={<VerticalRouteGuard allow="restaurant"><RestaurantFloorPlan /></VerticalRouteGuard>} />
          <Route path="calendar" element={<AgendaCalendarUnified />} />
        </Route>
        <Route path="locations" element={<Navigate to="/tenant/business/locations" replace />} />
        <Route path="integrations" element={<IntegrationCenter />} />
        <Route path="integrations/purposes" element={<IntegrationPurposes />} />
        <Route path="integrations/evolution" element={<EvolutionPanel />} />
        <Route path="integrations/proxies" element={<WhatsAppProxies />} />
        <Route path="integrations/lead-sources" element={<Navigate to="/tenant/marketing/origenes" replace />} />
        <Route path="integrations/lead-sources/:id" element={<LeadSourceDetail />} />
        <Route path="integrations/email" element={<EmailTemplates />} />

        {/* Inbox — Conversaciones (Sprint 25 ADR-040) */}
        <Route path="inbox" element={<InboxHubLayout />}>
          <Route index element={<Inbox />} />
          <Route path="open" element={<Inbox statusFilter="waiting_agent" />} />
          <Route path="closed" element={<Inbox statusFilter="closed" />} />
        </Route>
        {/* Sprint 58.1 (hotfix) — "Llamadas" ya no es una pantalla aparte:
            una llamada que coincide con un contacto se ve como un canal más
            dentro de su misma tarjeta en Conversaciones. */}
        <Route path="inbox/calls" element={<Navigate to="/tenant/inbox" replace />} />

        {/* Legacy redirects — AI routes moved to /tenant/ai/* (ADR-017) */}
        <Route path="integrations/ai-dashboard" element={<Navigate to="/tenant/ai/dashboard" replace />} />
        <Route path="integrations/ai-test" element={<Navigate to="/tenant/ai/console" replace />} />
        <Route path="integrations/ai-config" element={<Navigate to="/tenant/ai/config" replace />} />
        <Route path="integrations/ai-training" element={<Navigate to="/tenant/ai/training" replace />} />

        {/* AI Hub — Config IA (ADR-017) */}
        <Route path="ai" element={<AIHubLayout />}>
          <Route index element={<AIQuickStart />} />
          <Route path="inicio" element={<AIQuickStart />} />
          <Route path="dashboard" element={<AIDashboard />} />
          <Route path="console" element={<AITestConsole />} />
          <Route path="config" element={<AIRoutingConfig />} />
          <Route path="training" element={<AITrainingStudio />} />
          <Route path="knowledge" element={<KnowledgeLibrary />} />
          <Route path="skills" element={<AISkillBuilder />} />
          <Route path="tools" element={<AIToolRegistry />} />
          <Route path="tools/mcp" element={<MCPConnectorCenter />} />
          <Route path="orchestrator" element={<ActionOrchestrator />} />
          <Route path="triggers" element={<TriggerCenter />} />
          <Route path="voice-calls" element={<VoiceCallCenter />} />
          <Route path="observability" element={<Navigate to="/tenant/reports/observability" replace />} />
        </Route>

        <Route path="reports" element={<ReportsHubLayout />}>
          <Route path="status" element={<AttendanceStatus />} />
          <Route path="executive" element={<Navigate to="/tenant" replace />} />
          <Route path="observability" element={<ObservabilityCenter />} />
        </Route>

        
        {/* S89 — Email y WhatsApp son módulos propios (no hub Marketing) */}
        <Route path="email" element={<EmailHubLayout />}>
          <Route index element={<EmailHome />} />
          <Route path="nuevo" element={<EmailMassSendWizard />} />
          <Route path="envios" element={<MarketingEnvios channel="email" />} />
          <Route path="plantillas" element={<EmailTemplates forcedTab="templates" marketingEmbed />} />
          <Route path="dominio" element={<EmailTemplates forcedTab="dominio" marketingEmbed />} />
          <Route path="automaticos" element={<MarketingAutomations />} />
          <Route path="informes" element={<EmailInformes />} />
        </Route>
        <Route path="whatsapp" element={<WhatsAppHubLayout />}>
          <Route index element={<WhatsAppHome />} />
          <Route path="conexion" element={<WhatsAppConexion />} />
          <Route path="envios" element={<MarketingEnvios channel="whatsapp" />} />
          <Route path="plantillas" element={<MarketingTemplates />} />
          <Route path="informes" element={<WhatsAppInformes />} />
        </Route>

        <Route path="marketing" element={<MarketingHubLayout />}>
          <Route index element={<MarketingHome />} />
          <Route path="email" element={<Navigate to="/tenant/email" replace />} />
          <Route path="whatsapp" element={<Navigate to="/tenant/whatsapp" replace />} />
          <Route path="templates" element={<MarketingTemplates />} />
          <Route path="segments" element={<MarketingSegments />} />
          <Route path="automations" element={<MarketingAutomations />} />
          <Route path="create" element={<MarketingSimpleFlow />} />
          <Route path="campaigns" element={<CampaignBuilder />} />
          <Route path="contacts" element={<ContactManager />} />
          <Route path="audiences" element={<AudienceBuilder />} />
          <Route path="schedules" element={<CampaignScheduler />} />
          <Route path="delivery" element={<CampaignDelivery />} />
          <Route path="origenes" element={<LeadOriginsHub />} />
          <Route path="qr-campaigns" element={<LeadOriginsHub />} />
          <Route path="lead-capture" element={<Navigate to="/tenant/marketing/origenes" replace />} />
          <Route path="lead-capture/new" element={<Navigate to="/tenant/marketing/origenes" replace />} />
          <Route path="lead-capture/:captureId" element={<Navigate to="/tenant/marketing/origenes" replace />} />
        </Route>

        <Route path="social-ads" element={<SocialAdsHubLayout />}>
          <Route index element={<Navigate to="publicaciones" replace />} />
          <Route path="publicaciones" element={<SocialPublishing />} />
          <Route path="meta-ads" element={<MetaAdsConfig />} />
        </Route>

        <Route path="business" element={<BusinessHubLayout />}>
          <Route path="resumen" element={<BusinessResumen />} />
          <Route path="team" element={<TeamManager />} />
          <Route path="assignment" element={<CrmAssignment />} />
          <Route path="catalog" element={<CatalogHub />} />
          <Route path="locations" element={<Locations />} />
          <Route path="developments" element={<VerticalRouteGuard allow="real_estate"><DevelopmentManager /></VerticalRouteGuard>} />
          <Route path="hotel" element={<VerticalRouteGuard allow="hotel"><HotelDesk /></VerticalRouteGuard>} />
          <Route path="taller" element={<VerticalRouteGuard allow="workshop"><WorkshopDesk /></VerticalRouteGuard>} />
          <Route path="clinica" element={<VerticalRouteGuard allow="clinic"><ClinicDesk /></VerticalRouteGuard>} />
          <Route path="restaurante" element={<VerticalRouteGuard allow="restaurant"><RestaurantService /></VerticalRouteGuard>} />
          <Route path="spa" element={<VerticalRouteGuard allow="spa"><SpaDesk /></VerticalRouteGuard>} />
          <Route path="flows" element={<FlowBuilder />} />
          <Route path="profile" element={<BusinessVerticalProfile />} />
          <Route path="connections" element={<BusinessConnections />} />
          <Route path="permissions" element={<BusinessPermissions />} />
        </Route>

        <Route path="administracion" element={<AdministracionHubLayout />}>
          <Route path="pos" element={<PosHub />} />
          <Route path="mesa-de-servicio" element={<VerticalRouteGuard allow="real_estate"><WarrantyTickets /></VerticalRouteGuard>} />
          <Route path="condominal" element={<VerticalRouteGuard allow="real_estate"><CondoManagement /></VerticalRouteGuard>} />
        </Route>

        {/* Web builder (ADR-157) — gated by flags + plan entitlements */}
        <Route path="web" element={<WebDashboard />} />
        <Route path="web/sites/new" element={<WebSiteNew />} />
        <Route path="web/sites/:siteId" element={<WebSiteEditor />} />
        <Route path="web/sites/:siteId/domains" element={<WebDomains />} />
        <Route path="web/sites/:siteId/tracking" element={<WebTracking />} />
        <Route path="web/sites/:siteId/forms" element={<WebForms />} />
        <Route path="web/sites/:siteId/versions" element={<WebVersions />} />
      </Route>

      {/* Public marketing landings (no auth) */}
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/diagnostico" element={<LandingDiagnosis />} />
      {/* Brand / conversion page from landing-brand-refresh (assets + page only; no layout rewrite) */}
      <Route path="/producto" element={<CavernicolAppLanding />} />
      <Route path="/cavernicolapp" element={<CavernicolAppLanding />} />
      <Route path="/terminos" element={<TermsOfService />} />
      <Route path="/privacidad" element={<PrivacyPolicy />} />

      {/* Root redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}


function LocaleBridge({ children }) {
  const { user } = useAuth();
  return <LocaleProvider user={user}>{children}</LocaleProvider>;
}


function MarcaDelHost() {
  useEffect(() => {
    fetch("/api/public/marca")
      .then((r) => r.json())
      .then((m) => {
        if (!m?.es_de_socio) return;
        const href = m.favicon_url || m.logo_url;
        if (href) {
          const url = href + (href.includes("?") ? "&" : "?") + "v=" + Date.now();
          let link = document.querySelector("link[rel=\'icon\']");
          if (!link) {
            link = document.createElement("link");
            link.rel = "icon";
            document.head.appendChild(link);
          }
          link.href = url;
          document.querySelectorAll("link[rel=\'apple-touch-icon\']").forEach((el) => {
            el.href = url;
          });
        }
        if (m.brand_name) {
          document.title = String(document.title || "").replace(/CavernicolApp/g, m.brand_name);
        }
      })
      .catch(() => {});
  }, []);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <MarcaDelHost />
      <AuthProvider>
        <LocaleBridge>
        <SessionInactivityGuard />
        <AppRoutes />
        <Toaster position="top-right" />
        <CargaGlobal />
      </LocaleBridge>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
